import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  collection, query, where, orderBy, onSnapshot, doc
} from 'firebase/firestore';
import { db } from '../firebase';

// ─── Starter themes (10) + Premium themes (10 static + 5 dynamic = 15) ────────
// dynamic: true  →  mouse-reactive background (handled in Widget render)

export const THEMES = {
  // ── Starter (10) ──────────────────────────────────────────────────────────
  light: {
    key: 'light', label: 'Light', plan: 'starter',
    bg: '#f8fafc',
    cardBg: '#ffffff', cardBorder: '1px solid #f1f5f9', cardShadow: '0 1px 4px rgba(0,0,0,0.07)', cardRadius: '16px',
    nameColor: '#111827', dateColor: '#9ca3af', textColor: '#374151', textStyle: 'italic',
    starFill: '#f59e0b', starEmpty: '#e5e7eb',
    headerNameColor: '#111827', ratingColor: '#111827', subColor: '#6b7280',
    toggleBg: (a) => a ? '#7c3aed' : '#e5e7eb', toggleText: (a) => a ? '#fff' : '#374151',
    dotActive: '#7c3aed', dotInactive: '#e5e7eb',
    brandColor: '#c4b5fd', spinnerColor: '#7c3aed',
    avatarColors: ['#7c3aed','#4f46e5','#2563eb','#059669','#d97706','#dc2626'],
    previewBg: '#f8fafc', previewCard: '#ffffff', previewBorder: '1px solid #e5e7eb',
  },
  dark: {
    key: 'dark', label: 'Dark', plan: 'starter',
    bg: '#0f172a',
    cardBg: '#1e293b', cardBorder: '1px solid #334155', cardShadow: '0 2px 8px rgba(0,0,0,0.3)', cardRadius: '16px',
    nameColor: '#f1f5f9', dateColor: '#64748b', textColor: '#cbd5e1', textStyle: 'italic',
    starFill: '#fbbf24', starEmpty: '#334155',
    headerNameColor: '#f1f5f9', ratingColor: '#f1f5f9', subColor: '#94a3b8',
    toggleBg: (a) => a ? '#7c3aed' : '#1e293b', toggleText: (a) => a ? '#fff' : '#94a3b8',
    dotActive: '#7c3aed', dotInactive: '#334155',
    brandColor: '#4c1d95', spinnerColor: '#7c3aed',
    avatarColors: ['#7c3aed','#4f46e5','#2563eb','#059669','#d97706','#e11d48'],
    previewBg: '#0f172a', previewCard: '#1e293b', previewBorder: '1px solid #334155',
  },
  minimal: {
    key: 'minimal', label: 'Minimal', plan: 'starter',
    bg: '#ffffff',
    cardBg: '#ffffff', cardBorder: '1px solid #e5e7eb', cardShadow: 'none', cardRadius: '8px',
    nameColor: '#111827', dateColor: '#9ca3af', textColor: '#4b5563', textStyle: 'normal',
    starFill: '#f59e0b', starEmpty: '#e5e7eb',
    headerNameColor: '#111827', ratingColor: '#111827', subColor: '#9ca3af',
    toggleBg: (a) => a ? '#111827' : '#f3f4f6', toggleText: (a) => a ? '#fff' : '#6b7280',
    dotActive: '#111827', dotInactive: '#e5e7eb',
    brandColor: '#d1d5db', spinnerColor: '#374151',
    avatarColors: ['#374151','#4b5563','#6b7280','#111827','#1f2937','#374151'],
    previewBg: '#ffffff', previewCard: '#f9fafb', previewBorder: '1px solid #e5e7eb',
  },
  bold: {
    key: 'bold', label: 'Bold', plan: 'starter', cardBlur: true,
    bg: 'linear-gradient(135deg, #4c1d95 0%, #1e1b4b 100%)',
    cardBg: 'rgba(255,255,255,0.1)', cardBorder: '1px solid rgba(255,255,255,0.15)',
    cardShadow: '0 4px 20px rgba(0,0,0,0.2)', cardRadius: '20px',
    nameColor: '#ffffff', dateColor: 'rgba(255,255,255,0.5)', textColor: 'rgba(255,255,255,0.85)', textStyle: 'italic',
    starFill: '#fbbf24', starEmpty: 'rgba(255,255,255,0.2)',
    headerNameColor: '#ffffff', ratingColor: '#ffffff', subColor: 'rgba(255,255,255,0.6)',
    toggleBg: (a) => a ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)', toggleText: (a) => a ? '#fff' : 'rgba(255,255,255,0.5)',
    dotActive: '#ffffff', dotInactive: 'rgba(255,255,255,0.25)',
    brandColor: 'rgba(255,255,255,0.3)', spinnerColor: '#ffffff',
    avatarColors: ['rgba(255,255,255,0.25)','rgba(255,255,255,0.2)','rgba(255,255,255,0.3)','rgba(255,255,255,0.15)','rgba(255,255,255,0.25)','rgba(255,255,255,0.2)'],
    previewBg: '#4c1d95', previewCard: 'rgba(255,255,255,0.15)', previewBorder: '1px solid rgba(255,255,255,0.2)',
  },
  ocean: {
    key: 'ocean', label: 'Ocean', plan: 'starter', cardBlur: true,
    bg: 'linear-gradient(160deg, #0ea5e9 0%, #0369a1 100%)',
    cardBg: 'rgba(255,255,255,0.15)', cardBorder: '1px solid rgba(255,255,255,0.2)',
    cardShadow: '0 4px 16px rgba(0,0,0,0.15)', cardRadius: '18px',
    nameColor: '#ffffff', dateColor: 'rgba(255,255,255,0.6)', textColor: 'rgba(255,255,255,0.9)', textStyle: 'normal',
    starFill: '#fde68a', starEmpty: 'rgba(255,255,255,0.25)',
    headerNameColor: '#ffffff', ratingColor: '#ffffff', subColor: 'rgba(255,255,255,0.7)',
    toggleBg: (a) => a ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)', toggleText: (a) => a ? '#0369a1' : 'rgba(255,255,255,0.7)',
    dotActive: '#ffffff', dotInactive: 'rgba(255,255,255,0.3)',
    brandColor: 'rgba(255,255,255,0.35)', spinnerColor: '#ffffff',
    avatarColors: ['rgba(255,255,255,0.3)','rgba(255,255,255,0.2)','rgba(255,255,255,0.25)','rgba(255,255,255,0.3)','rgba(255,255,255,0.2)','rgba(255,255,255,0.25)'],
    previewBg: '#0ea5e9', previewCard: 'rgba(255,255,255,0.2)', previewBorder: '1px solid rgba(255,255,255,0.3)',
  },
  forest: {
    key: 'forest', label: 'Forest', plan: 'starter',
    bg: '#f0fdf4',
    cardBg: '#ffffff', cardBorder: '1px solid #bbf7d0', cardShadow: '0 2px 8px rgba(16,185,129,0.08)', cardRadius: '14px',
    nameColor: '#14532d', dateColor: '#86efac', textColor: '#166534', textStyle: 'normal',
    starFill: '#f59e0b', starEmpty: '#d1fae5',
    headerNameColor: '#14532d', ratingColor: '#14532d', subColor: '#4ade80',
    toggleBg: (a) => a ? '#16a34a' : '#dcfce7', toggleText: (a) => a ? '#fff' : '#15803d',
    dotActive: '#16a34a', dotInactive: '#bbf7d0',
    brandColor: '#86efac', spinnerColor: '#16a34a',
    avatarColors: ['#16a34a','#15803d','#166534','#14532d','#4ade80','#059669'],
    previewBg: '#f0fdf4', previewCard: '#ffffff', previewBorder: '1px solid #bbf7d0',
  },
  sunset: {
    key: 'sunset', label: 'Sunset', plan: 'starter',
    bg: 'linear-gradient(135deg, #fed7aa 0%, #fecaca 50%, #e9d5ff 100%)',
    cardBg: 'rgba(255,255,255,0.7)', cardBorder: '1px solid rgba(255,255,255,0.8)',
    cardShadow: '0 4px 16px rgba(251,146,60,0.15)', cardRadius: '18px',
    nameColor: '#7c2d12', dateColor: '#f97316', textColor: '#9a3412', textStyle: 'italic',
    starFill: '#f97316', starEmpty: 'rgba(249,115,22,0.2)',
    headerNameColor: '#7c2d12', ratingColor: '#7c2d12', subColor: '#c2410c',
    toggleBg: (a) => a ? '#ea580c' : 'rgba(255,255,255,0.5)', toggleText: (a) => a ? '#fff' : '#9a3412',
    dotActive: '#ea580c', dotInactive: 'rgba(234,88,12,0.2)',
    brandColor: '#fdba74', spinnerColor: '#ea580c',
    avatarColors: ['#ea580c','#dc2626','#7c3aed','#d97706','#0369a1','#059669'],
    previewBg: '#fed7aa', previewCard: 'rgba(255,255,255,0.7)', previewBorder: '1px solid rgba(255,255,255,0.8)',
  },
  midnight: {
    key: 'midnight', label: 'Midnight', plan: 'starter',
    bg: '#030712',
    cardBg: '#111827', cardBorder: '1px solid #1f2937', cardShadow: '0 0 0 1px #1f2937, 0 4px 24px rgba(0,0,0,0.5)', cardRadius: '16px',
    nameColor: '#f9fafb', dateColor: '#4b5563', textColor: '#9ca3af', textStyle: 'normal',
    starFill: '#eab308', starEmpty: '#1f2937',
    headerNameColor: '#f9fafb', ratingColor: '#f9fafb', subColor: '#4b5563',
    toggleBg: (a) => a ? '#6366f1' : '#1f2937', toggleText: (a) => a ? '#fff' : '#6b7280',
    dotActive: '#6366f1', dotInactive: '#1f2937',
    brandColor: '#1f2937', spinnerColor: '#6366f1',
    avatarColors: ['#6366f1','#8b5cf6','#ec4899','#f43f5e','#f97316','#eab308'],
    previewBg: '#030712', previewCard: '#111827', previewBorder: '1px solid #1f2937',
  },
  rose: {
    key: 'rose', label: 'Rose', plan: 'starter',
    bg: '#fff1f2',
    cardBg: '#ffffff', cardBorder: '1px solid #fecdd3', cardShadow: '0 2px 8px rgba(244,63,94,0.08)', cardRadius: '16px',
    nameColor: '#881337', dateColor: '#fda4af', textColor: '#9f1239', textStyle: 'italic',
    starFill: '#f43f5e', starEmpty: '#ffe4e6',
    headerNameColor: '#881337', ratingColor: '#881337', subColor: '#fb7185',
    toggleBg: (a) => a ? '#e11d48' : '#ffe4e6', toggleText: (a) => a ? '#fff' : '#9f1239',
    dotActive: '#e11d48', dotInactive: '#fecdd3',
    brandColor: '#fda4af', spinnerColor: '#e11d48',
    avatarColors: ['#e11d48','#f43f5e','#fb7185','#be123c','#9f1239','#881337'],
    previewBg: '#fff1f2', previewCard: '#ffffff', previewBorder: '1px solid #fecdd3',
  },
  slate: {
    key: 'slate', label: 'Slate', plan: 'starter',
    bg: '#f1f5f9',
    cardBg: '#ffffff', cardBorder: 'none', cardShadow: '0 1px 3px rgba(0,0,0,0.1), 0 4px 16px rgba(0,0,0,0.05)', cardRadius: '12px',
    nameColor: '#0f172a', dateColor: '#94a3b8', textColor: '#334155', textStyle: 'normal',
    starFill: '#f59e0b', starEmpty: '#e2e8f0',
    headerNameColor: '#0f172a', ratingColor: '#0f172a', subColor: '#64748b',
    toggleBg: (a) => a ? '#0f172a' : '#e2e8f0', toggleText: (a) => a ? '#fff' : '#475569',
    dotActive: '#0f172a', dotInactive: '#cbd5e1',
    brandColor: '#94a3b8', spinnerColor: '#0f172a',
    avatarColors: ['#0f172a','#1e293b','#334155','#475569','#64748b','#0ea5e9'],
    previewBg: '#f1f5f9', previewCard: '#ffffff', previewBorder: 'none',
  },

  // ── Premium Static (10) ───────────────────────────────────────────────────
  luxury: {
    key: 'luxury', label: 'Luxury', plan: 'premium',
    bg: '#18110a',
    cardBg: '#241a0e', cardBorder: '1px solid #b8973a', cardShadow: '0 4px 24px rgba(184,151,58,0.15)', cardRadius: '16px',
    nameColor: '#f5e6c8', dateColor: '#b8973a', textColor: '#d4bc8a', textStyle: 'italic',
    starFill: '#b8973a', starEmpty: '#3a2e1a',
    headerNameColor: '#f5e6c8', ratingColor: '#b8973a', subColor: '#8a6d2e',
    toggleBg: (a) => a ? '#b8973a' : '#2e2010', toggleText: (a) => a ? '#18110a' : '#b8973a',
    dotActive: '#b8973a', dotInactive: '#3a2e1a',
    brandColor: '#8a6d2e', spinnerColor: '#b8973a',
    avatarColors: ['#b8973a','#8a6d2e','#d4bc8a','#f5e6c8','#c9a84c','#a07828'],
    previewBg: '#18110a', previewCard: '#241a0e', previewBorder: '1px solid #b8973a',
  },
  coffee: {
    key: 'coffee', label: 'Coffee', plan: 'premium',
    bg: '#fdf8f4',
    cardBg: '#ffffff', cardBorder: '1px solid #e8d5c4', cardShadow: '0 2px 12px rgba(120,76,40,0.08)', cardRadius: '14px',
    nameColor: '#3b1f0a', dateColor: '#b07850', textColor: '#5c3a1e', textStyle: 'normal',
    starFill: '#c47c3a', starEmpty: '#e8d5c4',
    headerNameColor: '#3b1f0a', ratingColor: '#3b1f0a', subColor: '#8c5a30',
    toggleBg: (a) => a ? '#784c28' : '#f0e4d8', toggleText: (a) => a ? '#fff' : '#784c28',
    dotActive: '#784c28', dotInactive: '#e8d5c4',
    brandColor: '#c4a882', spinnerColor: '#784c28',
    avatarColors: ['#784c28','#a06838','#c47c3a','#5c3a1e','#3b1f0a','#b07850'],
    previewBg: '#fdf8f4', previewCard: '#ffffff', previewBorder: '1px solid #e8d5c4',
  },
  neon: {
    key: 'neon', label: 'Neon', plan: 'premium',
    bg: '#07000f',
    cardBg: '#0d0020', cardBorder: '1px solid #9333ea', cardShadow: '0 0 20px rgba(147,51,234,0.3), inset 0 0 20px rgba(147,51,234,0.05)', cardRadius: '16px',
    nameColor: '#e879f9', dateColor: '#7c3aed', textColor: '#d8b4fe', textStyle: 'normal',
    starFill: '#e879f9', starEmpty: '#2e1065',
    headerNameColor: '#e879f9', ratingColor: '#e879f9', subColor: '#a855f7',
    toggleBg: (a) => a ? '#9333ea' : '#1a0030', toggleText: (a) => a ? '#fff' : '#a855f7',
    dotActive: '#e879f9', dotInactive: '#2e1065',
    brandColor: '#7c3aed', spinnerColor: '#e879f9',
    avatarColors: ['#9333ea','#e879f9','#06b6d4','#22d3ee','#a855f7','#c084fc'],
    previewBg: '#07000f', previewCard: '#0d0020', previewBorder: '1px solid #9333ea',
  },
  lavender: {
    key: 'lavender', label: 'Lavender', plan: 'premium',
    bg: '#f5f3ff',
    cardBg: '#ffffff', cardBorder: '1px solid #ddd6fe', cardShadow: '0 2px 12px rgba(139,92,246,0.08)', cardRadius: '16px',
    nameColor: '#2e1065', dateColor: '#a78bfa', textColor: '#4c1d95', textStyle: 'italic',
    starFill: '#7c3aed', starEmpty: '#ede9fe',
    headerNameColor: '#2e1065', ratingColor: '#4c1d95', subColor: '#7c3aed',
    toggleBg: (a) => a ? '#7c3aed' : '#ede9fe', toggleText: (a) => a ? '#fff' : '#4c1d95',
    dotActive: '#7c3aed', dotInactive: '#ddd6fe',
    brandColor: '#c4b5fd', spinnerColor: '#7c3aed',
    avatarColors: ['#7c3aed','#8b5cf6','#a78bfa','#4c1d95','#6d28d9','#5b21b6'],
    previewBg: '#f5f3ff', previewCard: '#ffffff', previewBorder: '1px solid #ddd6fe',
  },
  coral: {
    key: 'coral', label: 'Coral', plan: 'premium',
    bg: '#fff7f5',
    cardBg: '#ffffff', cardBorder: '1px solid #ffd0c4', cardShadow: '0 2px 12px rgba(255,99,71,0.08)', cardRadius: '18px',
    nameColor: '#7f1d1d', dateColor: '#fb923c', textColor: '#9a3412', textStyle: 'normal',
    starFill: '#f97316', starEmpty: '#ffd0c4',
    headerNameColor: '#7f1d1d', ratingColor: '#7f1d1d', subColor: '#ea580c',
    toggleBg: (a) => a ? '#f97316' : '#fff0ec', toggleText: (a) => a ? '#fff' : '#9a3412',
    dotActive: '#f97316', dotInactive: '#ffd0c4',
    brandColor: '#fdba74', spinnerColor: '#f97316',
    avatarColors: ['#f97316','#ea580c','#fb923c','#dc2626','#9a3412','#c2410c'],
    previewBg: '#fff7f5', previewCard: '#ffffff', previewBorder: '1px solid #ffd0c4',
  },
  arctic: {
    key: 'arctic', label: 'Arctic', plan: 'premium',
    bg: 'linear-gradient(160deg, #e0f2fe 0%, #f0f9ff 50%, #e0e7ff 100%)',
    cardBg: 'rgba(255,255,255,0.75)', cardBorder: '1px solid rgba(186,230,253,0.8)',
    cardShadow: '0 4px 20px rgba(14,165,233,0.1)', cardRadius: '20px',
    nameColor: '#0c4a6e', dateColor: '#7dd3fc', textColor: '#075985', textStyle: 'normal',
    starFill: '#0ea5e9', starEmpty: 'rgba(14,165,233,0.2)',
    headerNameColor: '#0c4a6e', ratingColor: '#0c4a6e', subColor: '#38bdf8',
    toggleBg: (a) => a ? '#0ea5e9' : 'rgba(186,230,253,0.5)', toggleText: (a) => a ? '#fff' : '#0369a1',
    dotActive: '#0ea5e9', dotInactive: 'rgba(14,165,233,0.2)',
    brandColor: '#7dd3fc', spinnerColor: '#0ea5e9',
    avatarColors: ['#0ea5e9','#38bdf8','#7dd3fc','#0369a1','#0c4a6e','#6366f1'],
    previewBg: '#e0f2fe', previewCard: 'rgba(255,255,255,0.75)', previewBorder: '1px solid rgba(186,230,253,0.8)',
  },
  charcoal: {
    key: 'charcoal', label: 'Charcoal', plan: 'premium',
    bg: '#1c1c1e',
    cardBg: '#2c2c2e', cardBorder: '1px solid #3a3a3c', cardShadow: '0 4px 20px rgba(0,0,0,0.4)', cardRadius: '14px',
    nameColor: '#f2f2f7', dateColor: '#8e8e93', textColor: '#aeaeb2', textStyle: 'normal',
    starFill: '#ff9f0a', starEmpty: '#3a3a3c',
    headerNameColor: '#f2f2f7', ratingColor: '#f2f2f7', subColor: '#8e8e93',
    toggleBg: (a) => a ? '#636366' : '#3a3a3c', toggleText: (a) => a ? '#fff' : '#8e8e93',
    dotActive: '#636366', dotInactive: '#3a3a3c',
    brandColor: '#48484a', spinnerColor: '#ff9f0a',
    avatarColors: ['#ff9f0a','#ff6b6b','#30d158','#0a84ff','#bf5af2','#ff453a'],
    previewBg: '#1c1c1e', previewCard: '#2c2c2e', previewBorder: '1px solid #3a3a3c',
  },
  mint: {
    key: 'mint', label: 'Mint', plan: 'premium',
    bg: '#f0fdf9',
    cardBg: '#ffffff', cardBorder: '1px solid #99f6e4', cardShadow: '0 2px 12px rgba(20,184,166,0.08)', cardRadius: '16px',
    nameColor: '#134e4a', dateColor: '#5eead4', textColor: '#0f766e', textStyle: 'normal',
    starFill: '#14b8a6', starEmpty: '#ccfbf1',
    headerNameColor: '#134e4a', ratingColor: '#134e4a', subColor: '#2dd4bf',
    toggleBg: (a) => a ? '#0d9488' : '#ccfbf1', toggleText: (a) => a ? '#fff' : '#0f766e',
    dotActive: '#0d9488', dotInactive: '#99f6e4',
    brandColor: '#5eead4', spinnerColor: '#0d9488',
    avatarColors: ['#0d9488','#14b8a6','#2dd4bf','#0f766e','#134e4a','#059669'],
    previewBg: '#f0fdf9', previewCard: '#ffffff', previewBorder: '1px solid #99f6e4',
  },
  noir: {
    key: 'noir', label: 'Noir', plan: 'premium',
    bg: '#000000',
    cardBg: '#0a0a0a', cardBorder: '1px solid #222222', cardShadow: '0 0 0 1px #1a1a1a', cardRadius: '0px',
    nameColor: '#ffffff', dateColor: '#555555', textColor: '#aaaaaa', textStyle: 'normal',
    starFill: '#ffffff', starEmpty: '#222222',
    headerNameColor: '#ffffff', ratingColor: '#ffffff', subColor: '#555555',
    toggleBg: (a) => a ? '#ffffff' : '#111111', toggleText: (a) => a ? '#000000' : '#555555',
    dotActive: '#ffffff', dotInactive: '#222222',
    brandColor: '#333333', spinnerColor: '#ffffff',
    avatarColors: ['#ffffff','#cccccc','#aaaaaa','#888888','#666666','#444444'],
    previewBg: '#000000', previewCard: '#0a0a0a', previewBorder: '1px solid #222222',
  },
  candy: {
    key: 'candy', label: 'Candy', plan: 'premium',
    bg: 'linear-gradient(135deg, #fce7f3 0%, #ede9fe 50%, #e0f2fe 100%)',
    cardBg: 'rgba(255,255,255,0.8)', cardBorder: '1px solid rgba(255,255,255,0.9)',
    cardShadow: '0 4px 20px rgba(236,72,153,0.1)', cardRadius: '24px',
    nameColor: '#831843', dateColor: '#f472b6', textColor: '#9d174d', textStyle: 'normal',
    starFill: '#ec4899', starEmpty: 'rgba(236,72,153,0.2)',
    headerNameColor: '#831843', ratingColor: '#831843', subColor: '#db2777',
    toggleBg: (a) => a ? '#ec4899' : 'rgba(255,255,255,0.6)', toggleText: (a) => a ? '#fff' : '#9d174d',
    dotActive: '#ec4899', dotInactive: 'rgba(236,72,153,0.2)',
    brandColor: '#fbcfe8', spinnerColor: '#ec4899',
    avatarColors: ['#ec4899','#f472b6','#8b5cf6','#06b6d4','#f97316','#a3e635'],
    previewBg: '#fce7f3', previewCard: 'rgba(255,255,255,0.8)', previewBorder: '1px solid rgba(255,255,255,0.9)',
  },

  // ── Premium Dynamic — mouse-reactive (5) ─────────────────────────────────
  aurora: {
    key: 'aurora', label: 'Aurora', plan: 'premium', dynamic: true,
    // bg is computed from mouse position in Widget render
    bg: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    cardBg: 'rgba(255,255,255,0.08)', cardBorder: '1px solid rgba(100,220,180,0.25)',
    cardShadow: '0 4px 24px rgba(100,220,180,0.1)', cardRadius: '18px',
    nameColor: '#a0ffd6', dateColor: 'rgba(160,255,214,0.5)', textColor: 'rgba(200,255,230,0.85)', textStyle: 'normal',
    starFill: '#00ffab', starEmpty: 'rgba(0,255,171,0.15)',
    headerNameColor: '#a0ffd6', ratingColor: '#a0ffd6', subColor: 'rgba(160,255,214,0.6)',
    toggleBg: (a) => a ? 'rgba(0,255,171,0.3)' : 'rgba(255,255,255,0.05)', toggleText: (a) => a ? '#001a0d' : 'rgba(160,255,214,0.6)',
    dotActive: '#00ffab', dotInactive: 'rgba(0,255,171,0.15)',
    brandColor: 'rgba(0,255,171,0.3)', spinnerColor: '#00ffab',
    avatarColors: ['#00ffab','#00e5ff','#7c4dff','#64ffda','#1de9b6','#00bfa5'],
    previewBg: '#1a1a2e', previewCard: 'rgba(255,255,255,0.08)', previewBorder: '1px solid rgba(100,220,180,0.25)',
    // Dynamic gradient: shifts aurora colors based on mouse X/Y
    getDynamicBg: (mx, my) => `radial-gradient(ellipse at ${mx}% ${my}%, #0d4f3c 0%, #1a1a2e 40%, #0f3460 70%, #16213e 100%)`,
  },
  prism: {
    key: 'prism', label: 'Prism', plan: 'premium', dynamic: true,
    bg: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
    cardBg: 'rgba(255,255,255,0.07)', cardBorder: '1px solid rgba(255,255,255,0.15)',
    cardShadow: '0 4px 24px rgba(99,102,241,0.2)', cardRadius: '20px',
    nameColor: '#ffffff', dateColor: 'rgba(255,255,255,0.5)', textColor: 'rgba(255,255,255,0.8)', textStyle: 'normal',
    starFill: '#818cf8', starEmpty: 'rgba(255,255,255,0.1)',
    headerNameColor: '#ffffff', ratingColor: '#c7d2fe', subColor: 'rgba(199,210,254,0.7)',
    toggleBg: (a) => a ? 'rgba(129,140,248,0.4)' : 'rgba(255,255,255,0.05)', toggleText: (a) => a ? '#fff' : 'rgba(255,255,255,0.5)',
    dotActive: '#818cf8', dotInactive: 'rgba(255,255,255,0.15)',
    brandColor: 'rgba(129,140,248,0.4)', spinnerColor: '#818cf8',
    avatarColors: ['#818cf8','#a78bfa','#f472b6','#34d399','#fbbf24','#60a5fa'],
    previewBg: '#1e1b4b', previewCard: 'rgba(255,255,255,0.07)', previewBorder: '1px solid rgba(255,255,255,0.15)',
    getDynamicBg: (mx, my) => {
      const hue = Math.round((mx / 100) * 60 + 240);
      const hue2 = Math.round((my / 100) * 60 + 280);
      return `radial-gradient(ellipse at ${mx}% ${my}%, hsl(${hue},70%,25%) 0%, #1e1b4b 50%, hsl(${hue2},80%,15%) 100%)`;
    },
  },
  ember: {
    key: 'ember', label: 'Ember', plan: 'premium', dynamic: true,
    bg: 'linear-gradient(135deg, #1a0500 0%, #3d0c00 100%)',
    cardBg: 'rgba(255,80,20,0.08)', cardBorder: '1px solid rgba(255,100,30,0.2)',
    cardShadow: '0 4px 24px rgba(255,60,10,0.15)', cardRadius: '16px',
    nameColor: '#ffb38a', dateColor: 'rgba(255,140,80,0.5)', textColor: 'rgba(255,200,160,0.85)', textStyle: 'normal',
    starFill: '#ff6820', starEmpty: 'rgba(255,104,32,0.15)',
    headerNameColor: '#ffb38a', ratingColor: '#ff6820', subColor: 'rgba(255,140,80,0.6)',
    toggleBg: (a) => a ? 'rgba(255,104,32,0.4)' : 'rgba(255,80,20,0.08)', toggleText: (a) => a ? '#fff' : 'rgba(255,140,80,0.7)',
    dotActive: '#ff6820', dotInactive: 'rgba(255,104,32,0.15)',
    brandColor: 'rgba(255,104,32,0.4)', spinnerColor: '#ff6820',
    avatarColors: ['#ff6820','#ff9040','#ffb870','#dc2626','#f97316','#fbbf24'],
    previewBg: '#1a0500', previewCard: 'rgba(255,80,20,0.08)', previewBorder: '1px solid rgba(255,100,30,0.2)',
    getDynamicBg: (mx, my) => `radial-gradient(ellipse at ${mx}% ${my}%, #7c1900 0%, #3d0c00 40%, #1a0500 100%)`,
  },
  galaxy: {
    key: 'galaxy', label: 'Galaxy', plan: 'premium', dynamic: true,
    bg: '#020010',
    cardBg: 'rgba(120,80,220,0.08)', cardBorder: '1px solid rgba(120,80,220,0.2)',
    cardShadow: '0 4px 24px rgba(100,60,200,0.2)', cardRadius: '18px',
    nameColor: '#e0d0ff', dateColor: 'rgba(180,150,255,0.5)', textColor: 'rgba(220,200,255,0.85)', textStyle: 'normal',
    starFill: '#c084fc', starEmpty: 'rgba(192,132,252,0.15)',
    headerNameColor: '#e0d0ff', ratingColor: '#c084fc', subColor: 'rgba(192,132,252,0.6)',
    toggleBg: (a) => a ? 'rgba(192,132,252,0.3)' : 'rgba(120,80,220,0.08)', toggleText: (a) => a ? '#fff' : 'rgba(180,150,255,0.6)',
    dotActive: '#c084fc', dotInactive: 'rgba(192,132,252,0.15)',
    brandColor: 'rgba(192,132,252,0.3)', spinnerColor: '#c084fc',
    avatarColors: ['#c084fc','#a855f7','#7c3aed','#e879f9','#818cf8','#60a5fa'],
    previewBg: '#020010', previewCard: 'rgba(120,80,220,0.08)', previewBorder: '1px solid rgba(120,80,220,0.2)',
    getDynamicBg: (mx, my) => `radial-gradient(ellipse at ${mx}% ${my}%, #3b0764 0%, #1e1040 40%, #020010 100%)`,
  },
  holo: {
    key: 'holo', label: 'Holo', plan: 'premium', dynamic: true,
    bg: 'linear-gradient(135deg, #f0f4ff 0%, #fdf0ff 50%, #f0fff8 100%)',
    cardBg: 'rgba(255,255,255,0.6)', cardBorder: '1px solid rgba(200,200,255,0.5)',
    cardShadow: '0 4px 24px rgba(150,100,255,0.12)', cardRadius: '20px',
    nameColor: '#2d1b69', dateColor: 'rgba(100,60,200,0.5)', textColor: '#3d2b79', textStyle: 'normal',
    starFill: '#7c3aed', starEmpty: 'rgba(124,58,237,0.15)',
    headerNameColor: '#2d1b69', ratingColor: '#5b21b6', subColor: 'rgba(100,60,200,0.6)',
    toggleBg: (a) => a ? '#7c3aed' : 'rgba(200,200,255,0.3)', toggleText: (a) => a ? '#fff' : '#3d2b79',
    dotActive: '#7c3aed', dotInactive: 'rgba(124,58,237,0.15)',
    brandColor: 'rgba(124,58,237,0.3)', spinnerColor: '#7c3aed',
    avatarColors: ['#7c3aed','#ec4899','#06b6d4','#10b981','#f59e0b','#6366f1'],
    previewBg: '#f0f4ff', previewCard: 'rgba(255,255,255,0.6)', previewBorder: '1px solid rgba(200,200,255,0.5)',
    getDynamicBg: (mx, my) => {
      const hue1 = Math.round((mx / 100) * 120 + 200);
      const hue2 = Math.round((my / 100) * 120 + 280);
      const hue3 = Math.round(((mx + my) / 200) * 120 + 160);
      return `radial-gradient(ellipse at ${mx}% ${my}%, hsl(${hue1},80%,92%) 0%, hsl(${hue2},70%,95%) 40%, hsl(${hue3},80%,93%) 100%)`;
    },
  },
};

// All theme keys
export const ALL_THEMES = Object.keys(THEMES);

// ─── Custom theme builder ─────────────────────────────────────────────────────
// Default draft shape for a new custom theme
export const DEFAULT_CUSTOM_DRAFT = {
  darkMode: false,
  bgType: 'solid',         // 'solid' | 'lr-gradient' | 'diag-gradient'
  bgColor1: '#f8fafc',
  bgColor2: '#e0e7ff',
  cardBg: '#ffffff',
  cardRadius: 'rounded',   // 'sharp' | 'rounded' | 'pill'
  textColor: '#374151',
  starColor: '#f59e0b',
  nameColor: '#111827',
};

// Converts a customDraft object into a full theme object compatible with Widget
export function buildCustomThemeObject(draft) {
  if (!draft) return THEMES.light;
  const isDark = draft.darkMode;

  let bg;
  if (draft.bgType === 'lr-gradient') {
    bg = `linear-gradient(90deg, ${draft.bgColor1} 0%, ${draft.bgColor2} 100%)`;
  } else if (draft.bgType === 'diag-gradient') {
    bg = `linear-gradient(135deg, ${draft.bgColor1} 0%, ${draft.bgColor2} 100%)`;
  } else {
    bg = draft.bgColor1;
  }

  const radiusMap = { sharp: '4px', rounded: '16px', pill: '24px' };
  const cardRadius = radiusMap[draft.cardRadius] || '16px';

  const accent = draft.starColor;
  const dateColor   = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.38)';
  const subColor    = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)';
  const cardBorder  = isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)';
  const cardShadow  = isDark ? '0 4px 20px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.07)';
  const starEmpty   = isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)';
  const dotInactive = isDark ? 'rgba(255,255,255,0.2)'  : 'rgba(0,0,0,0.12)';
  const brandColor  = isDark ? 'rgba(255,255,255,0.2)'  : 'rgba(0,0,0,0.2)';

  return {
    key: 'custom',
    label: 'Custom',
    bg,
    cardBg: draft.cardBg,
    cardBorder,
    cardShadow,
    cardRadius,
    nameColor: draft.nameColor,
    dateColor,
    textColor: draft.textColor,
    textStyle: 'normal',
    starFill: accent,
    starEmpty,
    headerNameColor: draft.nameColor,
    ratingColor: draft.nameColor,
    subColor,
    toggleBg:   (a) => a ? accent : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)'),
    toggleText: (a) => a ? '#fff' : (isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.45)'),
    dotActive: accent,
    dotInactive,
    brandColor,
    spinnerColor: accent,
    avatarColors: [accent, draft.nameColor, '#6366f1', '#059669', '#d97706', '#dc2626'],
    previewBg:     draft.bgColor1,
    previewCard:   draft.cardBg,
    previewBorder: cardBorder,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function StarRow({ rating, theme }) {
  return (
    <div style={{ display: 'flex', gap: '2px' }}>
      {[1,2,3,4,5].map(s => (
        <svg key={s} width="14" height="14" viewBox="0 0 24 24"
          fill={s <= rating ? theme.starFill : theme.starEmpty}
          xmlns="http://www.w3.org/2000/svg">
          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
        </svg>
      ))}
    </div>
  );
}

function TestimonialCard({ t, theme }) {
  const color = theme.avatarColors[(t.name?.charCodeAt(0) || 0) % theme.avatarColors.length];
  const initials = t.name
    ? t.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <div style={{
      background: theme.cardBg,
      borderRadius: theme.cardRadius,
      padding: '20px',
      border: theme.cardBorder,
      boxShadow: theme.cardShadow,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      breakInside: 'avoid',
      marginBottom: '16px',
      backdropFilter: theme.cardBlur ? 'blur(10px)' : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          width: '36px', height: '36px', borderRadius: '50%',
          background: color, display: 'flex', alignItems: 'center',
          justifyContent: 'center', color: '#fff', fontWeight: 700,
          fontSize: '13px', flexShrink: 0,
        }}>
          {initials}
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: '14px', color: theme.nameColor }}>{t.name}</div>
          <div style={{ fontSize: '11px', color: theme.dateColor }}>
            {t.createdAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) || ''}
          </div>
        </div>
      </div>
      <StarRow rating={t.rating} theme={theme} />
      <p style={{ fontSize: '13px', color: theme.textColor, lineHeight: '1.65', margin: 0, fontStyle: theme.textStyle }}>
        "{t.message}"
      </p>
    </div>
  );
}

function CarouselView({ testimonials, theme }) {
  const [current, setCurrent] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (testimonials.length <= 1) return;
    intervalRef.current = setInterval(() => setCurrent(c => (c + 1) % testimonials.length), 5000);
    return () => clearInterval(intervalRef.current);
  }, [testimonials.length]);

  if (!testimonials.length) return null;

  return (
    <div style={{ padding: '0 4px' }}>
      <TestimonialCard t={testimonials[current]} theme={theme} />
      {testimonials.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '4px' }}>
          {testimonials.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)} style={{
              width: i === current ? '20px' : '8px', height: '8px',
              borderRadius: '4px', border: 'none',
              background: i === current ? theme.dotActive : theme.dotInactive,
              cursor: 'pointer', transition: 'all 0.3s', padding: 0,
            }} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Widget export ───────────────────────────────────────────────────────
export default function Widget() {
  const { businessId } = useParams();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('grid');
  const [businessName, setBusinessName] = useState('');
  const [themeKey, setThemeKey] = useState('light');
  const [customThemeData, setCustomThemeData] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 50, y: 30 });
  const containerRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('view') === 'carousel') setView('carousel');
  }, []);

  // Live-listen to business profile — theme and name update instantly
  useEffect(() => {
    if (!businessId) return;
    const unsub = onSnapshot(doc(db, 'businesses', businessId), snap => {
      const data = snap.data() || {};
      setBusinessName(data.businessName || '');
      setThemeKey(data.theme || 'light');
      if (data.customTheme) setCustomThemeData(data.customTheme);
    });
    return unsub;
  }, [businessId]);

  // Live-listen to approved testimonials — new approvals appear instantly
  useEffect(() => {
    const bId = businessId || 'default';
    const q = query(
      collection(db, 'businesses', bId, 'testimonials'),
      where('status', '==', 'approved'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setTestimonials(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [businessId]);

  const theme = themeKey === 'custom'
    ? buildCustomThemeObject(customThemeData)
    : (THEMES[themeKey] || THEMES.light);

  // Mouse tracking for dynamic themes
  useEffect(() => {
    if (!theme.dynamic) return;
    const el = containerRef.current;
    if (!el) return;
    const handleMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
      const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
      setMousePos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
    };
    el.addEventListener('mousemove', handleMove);
    return () => el.removeEventListener('mousemove', handleMove);
  }, [theme.dynamic]);

  const dynamicBg = theme.dynamic && theme.getDynamicBg
    ? theme.getDynamicBg(mousePos.x, mousePos.y)
    : theme.bg;

  const avgRating = testimonials.length
    ? (testimonials.reduce((s, t) => s + (t.rating || 0), 0) / testimonials.length).toFixed(1)
    : null;

  return (
    <div ref={containerRef} style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: theme.dynamic ? dynamicBg : theme.bg,
      transition: theme.dynamic ? 'background 0.3s ease' : 'none',
      minHeight: '100vh',
      padding: '20px 16px',
      boxSizing: 'border-box',
    }}>
      <style>{`@keyframes tw-spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      {testimonials.length > 0 && (
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          {businessName && (
            <div style={{ fontSize: '16px', fontWeight: 700, color: theme.headerNameColor, marginBottom: '6px' }}>
              {businessName}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '2px' }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: theme.ratingColor, lineHeight: 1 }}>
              {avgRating}
            </div>
            <div style={{ display: 'flex', gap: '2px' }}>
              {[1,2,3,4,5].map(s => (
                <svg key={s} width="18" height="18" viewBox="0 0 24 24"
                  fill={s <= Math.round(avgRating) ? theme.starFill : theme.starEmpty}
                  xmlns="http://www.w3.org/2000/svg">
                  <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                </svg>
              ))}
            </div>
          </div>
          <div style={{ fontSize: '12px', color: theme.subColor }}>
            Based on {testimonials.length} review{testimonials.length !== 1 ? 's' : ''}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '12px' }}>
            {['grid', 'carousel'].map(v => (
              <button key={v} onClick={() => setView(v)} style={{
                padding: '4px 14px', borderRadius: '20px', border: 'none',
                background: theme.toggleBg(view === v),
                color: theme.toggleText(view === v),
                fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                textTransform: 'capitalize', transition: 'all 0.2s',
              }}>{v}</button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '40px' }}>
          <div style={{
            width: '32px', height: '32px',
            border: `3px solid ${theme.spinnerColor}`,
            borderTopColor: 'transparent', borderRadius: '50%',
            animation: 'tw-spin 0.8s linear infinite',
          }} />
        </div>
      ) : testimonials.length === 0 ? (
        <div style={{ textAlign: 'center', paddingTop: '40px', color: theme.subColor }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill={theme.starEmpty}
            style={{ margin: '0 auto 12px', display: 'block' }} xmlns="http://www.w3.org/2000/svg">
            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
          </svg>
          <p style={{ margin: 0, fontSize: '14px' }}>No reviews yet</p>
        </div>
      ) : view === 'carousel' ? (
        <CarouselView testimonials={testimonials} theme={theme} />
      ) : (
        <div style={{ columns: '280px', columnGap: '16px' }}>
          {testimonials.map(t => <TestimonialCard key={t.id} t={t} theme={theme} />)}
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: '16px' }}>
        <a href={window.location.origin} target="_blank" rel="noreferrer"
          style={{ fontSize: '11px', color: theme.brandColor, textDecoration: 'none' }}>
          Powered by TestimonialWall
        </a>
      </div>
    </div>
  );
}
